/**
 * Why a shopper cannot buy something, most-recoverable first.
 *
 * Only OUT_OF_STOCK and LISTING_PAUSED accept a stock alert — the other two are blocked on
 * review or vendor standing, which no restock will fix, and the API refuses those watches.
 */
export type UnavailableReason =
  | 'OUT_OF_STOCK'
  | 'LISTING_PAUSED'
  | 'NOT_APPROVED'
  | 'VENDOR_UNAVAILABLE';

/**
 * Purchasable pack/size variant on a commerce product (Food + Pharmacy). Mirrors the backend
 * FoodProductVariant / PharmacyProductVariant — the authoritative price lives here, not on the
 * product. `discount_pct` is derived server-side from `mrp_inr` vs `price_inr`.
 */
export type ProductVariant = {
  id: string;
  product_id: string;
  label: string;
  price_inr: string;
  mrp_inr: string | null;
  discount_pct: number | null;
  display_order: number;
  /** Stock, as the caller's audience is allowed to see it. */
  is_in_stock?: boolean;
  /**
   * Whether this size can actually be BOUGHT: stock AND the listing switched on AND approved
   * AND the vendor in good standing. `is_in_stock` answers only the count, so a size sitting on
   * the shelf under a paused listing reports `is_in_stock: true` while being unbuyable. Grey
   * chips and gate "Notify me" off THIS flag.
   */
  is_purchasable?: boolean;
  /** Why it cannot be bought, or null when it can. */
  unavailable_reason?: UnavailableReason | null;
  stock_qty?: number | null;
  is_low_stock?: boolean;
  is_stock_tracked?: boolean;
  created_at: string;
  updated_at: string;
};

export type CreateVariantInput = { label: string; price_inr: number; mrp_inr?: number | null; display_order?: number };
export type UpdateVariantInput = Partial<CreateVariantInput>;

/** A variant while it's being typed into a form (strings before coercion/validation). */
export type VariantDraft = { label: string; price_inr: string; mrp_inr: string };

export const EMPTY_VARIANT_DRAFT: VariantDraft = { label: '', price_inr: '', mrp_inr: '' };

/** Client-side derived discount %, matching the server formula (null unless MRP > price). */
export function derive_discount_pct(price: number, mrp: number | null): number | null {
  if (mrp == null || !(mrp > price)) return null;
  return Math.round(((mrp - price) / mrp) * 100);
}

/**
 * Parse variant drafts into API inputs; returns an error string if any row is invalid.
 *
 * Shared because every surface that creates a product — the food form, the pharmacy
 * form, the catalogue picker — needs exactly these rules, and three copies would
 * drift into three different definitions of a valid price.
 */
export function parse_variant_drafts(
  drafts: VariantDraft[],
): { variants: CreateVariantInput[] } | { error: string } {
  if (drafts.length === 0) return { error: 'Add at least one variant.' };
  const variants: CreateVariantInput[] = [];
  for (const d of drafts) {
    if (!d.label.trim()) return { error: 'Every variant needs a label.' };
    const price_inr = Number(d.price_inr);
    if (!Number.isFinite(price_inr) || price_inr <= 0) return { error: `Enter a valid price for “${d.label || 'variant'}”.` };
    const mrp_inr = d.mrp_inr.trim() === '' ? null : Number(d.mrp_inr);
    if (mrp_inr != null && (!Number.isFinite(mrp_inr) || mrp_inr < price_inr)) return { error: `MRP must be ≥ price for “${d.label}”.` };
    variants.push({ label: d.label.trim(), price_inr, mrp_inr });
  }
  return { variants };
}

/**
 * Stock on a variant, as each audience is told it.
 *
 * `is_in_stock` is the only field a customer payload carries — the exact count is a
 * nudge to a vendor and a pressure tactic to a shopper, and it exposes a competitor's
 * inventory to anyone who looks.
 */
export type VariantStockFields = {
  is_in_stock: boolean;
  /** Vendor views only. Null = not tracked. */
  stock_qty?: number | null;
  is_low_stock?: boolean;
  is_stock_tracked?: boolean;
};
